import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  useTheme,
  Skeleton,
  Pagination,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { get_recent_activity_types, get_recent_activity_deatils } from 'src/ApiServices';

const RecentActivitiesPage = () => {
  const theme = useTheme();
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });
  const [loading, setLoading] = useState(true);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [page, setPage] = useState(1);
  const [inquiryType, setInquiryType] = useState('all');
  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);

  const fetchActivityTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await get_recent_activity_types();
      const types = [{ value: 'all', label: 'All' }];

      if (response?.data?.length > 0) {
        response.data.forEach((item) => {
          types.push({
            value: item.reference_name,
            label: item.reference_name
          });
        });
      }

      setActivityTypes(types);
    } catch (error) {
      console.error('Error fetching activity types:', error);
      toast.error('Failed to load activity types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchActivitiesData = async (currentPage = 1, type = 'all') => {
    try {
      setLoading(true);
      const params = { page: currentPage };

      if (type !== 'all') {
        params.filter = type;
      }

      const response = await get_recent_activity_deatils(params);

      if (response?.data) {
        setActivities(response.data);

        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setActivities([]);
      }
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      toast.error('Failed to load recent activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityTypes();
  }, []);

  useEffect(() => {
    if (!loadingTypes) {
      fetchActivitiesData(page, inquiryType);
    }
  }, [page, inquiryType, loadingTypes]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleTypeChange = (event) => {
    setInquiryType(event.target.value);
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tableHeaders = ['Date', 'Source', 'Logs', 'Description'];

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {tableHeaders.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: 2 }}>
            <Skeleton variant="text" height={25} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderNoRecordsFound = () => (
    <TableRow>
      <TableCell
        colSpan={tableHeaders.length}
        sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            No Recent Activities Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            {inquiryType !== 'all'
              ? `No ${inquiryType.toLowerCase()} activities found.`
              : 'No recent activities available at the moment.'}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const ActivityTable = () => (
    <Card>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table
          sx={{
            minWidth: 650,
            '& .MuiTableCell-head': {
              backgroundColor: palette.background.paper,
              color: palette.primary.tableHeadTitle,
              fontFamily: 'poppinsSemiBold',
              fontSize: '14px',
              borderBottom: `2px solid ${palette.divider}`,
              py: 2
            },
            '& .MuiTableCell-body': {
              color: palette.text.primary,
              fontFamily: 'poppinsRegular',
              fontSize: '14px',
              borderBottom: `1px solid ${palette.divider}`,
              py: 2
            }
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeaders.map((header, index) => (
                <TableCell key={index} sx={{ px: 2, whiteSpace: 'nowrap' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? renderSkeletonRows()
              : activities.length > 0
              ? activities.map((activity) => (
                  <TableRow
                    key={activity.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: palette.action.hover
                      }
                    }}
                  >
                    <TableCell sx={{ px: 2 }}>{formatDate(activity.created_at)}</TableCell>
                    <TableCell sx={{ px: 2 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'poppinsMedium' }}>
                        {activity.reference_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2 }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {activity.action || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2 }}>{activity.description || '-'}</TableCell>
                  </TableRow>
                ))
              : renderNoRecordsFound()}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );

  return (
    <Page title={`Recent Activities | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Recent Activities
          </Typography>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth size="medium" disabled={loadingTypes}>
              <InputLabel id="inquiry-type-label">Inquiry Type</InputLabel>
              <Select
                labelId="inquiry-type-label"
                value={inquiryType}
                label="Inquiry Type"
                onChange={handleTypeChange}
                sx={{
                  fontFamily: 'poppinsMedium',
                  backgroundColor: theme.palette.background.paper,
                  minWidth: 220
                  // height: 50
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200
                    }
                  }
                }}
                startAdornment={
                  loadingTypes ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : null
                }
              >
                {activityTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Box>

        <ActivityTable />

        {!loading && pagination.total > 0 && pagination.last_page >= 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </Page>
  );
};

export default RecentActivitiesPage;
